import React from 'react';
import './toolbar.css';
import {
  CloudUpload as CloudUploadIcon,
  Wallpaper as WallpaperIcon,
  Movie as MovieIcon,
  Person as PersonIcon,
  Audiotrack as AudiotrackIcon,
  Build as BuildIcon,
  TextFields as TextFieldsIcon,
  Animation as AnimationIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowRightAlt as ArrowRightAltIcon,
  KeyboardBackspace as KeyboardBackspaceIcon,
  PlayArrow as PlayArrowIcon,
  FiberManualRecord as RecordIcon,
  Pause as PauseIcon,
  Loop as LoopIcon,
  Height as HeightIcon,
  TrendingDown as TrendingDownIcon,
  SouthEast as SouthEastIcon,
  PersonAdd as PersonAddIcon,
  VideoCall as VideoCallIcon,
  Landscape as LandscapeIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';

import Menu from '../navigationmenu/NavigationMenu';
import { isHiddenTilesEnabled } from '../../services/featureFlagsService';
import { GeneratedAudioResult } from '../../services/aiAudioService';
import {
  AssetType,
  CharacterRef,
  FontFamily,
  FontStyle,
  FontWeight,
  Position,
  TextRef,
  RiggedCharacterRef,
} from '../../types/typing';

export interface TextTemplate {
  label: string;
  text: string;
  styleType?: 'default' | 'bubble';
  fontProperties: {
    fontFamily: FontFamily;
    fontSize: number;
    fontColor: string;
    fontStyle: FontStyle;
    fontWeight: FontWeight;
  };
}

/** All tiles rendered in the left toolbar/nav bar, in display order. */
const TOOLBAR_TILES = [
  'riggedCharacters',
  'environments',
  'characterVideo',
  'liveAction',
  'audio',
  'assets',
  'backgrounds',
  'characters',
  'props',
  'effects',
  'text',
];

/**
 * Tiles hidden from the nav bar when the FEATURE_FLAG_HIDDEN_TILES
 * feature flag is enabled.
 */
const TILES_HIDDEN_BY_FLAG = [
  'backgrounds',
  'characters',
  'props',
  'effects',
  'text',
];

/** Short captions used by the labelled toolbar variant. */
const TILE_LABEL: Record<string, string> = {
  riggedCharacters: 'Character',
  environments: 'Environment',
  characterVideo: 'Video',
  liveAction: 'Live Action',
  audio: 'Audio',
  assets: 'Assets',
  backgrounds: 'Backdrop',
  characters: 'People',
  props: 'Props',
  effects: 'Effects',
  text: 'Text',
};

interface ToolBarProps {
  onBackgroundSelect: (imageData: string | null) => void;
  onAnimationSelect: (animationData: any) => void;
  onEffectSelect: (effect: any) => void;
  onUploadedAssetSelect: (assetUrl: string) => void;
  onTextSelect: (template?: TextTemplate) => void;
  onLayerDrag: (draggedIndex: number, droppedIndex: number) => void;
  layers: any;
  charactersRef: React.MutableRefObject<Map<string, CharacterRef>>;
  textRef: React.MutableRefObject<Map<string, TextRef>>;
  handleLayerLock: (index: number) => void;
  selectedMenu: any;
  onMenuClick: (menu: string) => void;
  onMenuClose: () => void;
  onAudioSelect: (audio: any) => void;
  onUpdateCharacter: (
    id: string,
    properties: {
      position?: { x: number; y: number };
      scale?: number;
      pathPoints?: Position[];
    }
  ) => void;
  onUpdateText: (id: string, textProps: Partial<TextRef>) => void;
  onUpdateIsRecording: (isRecording: boolean) => void;
  onUpdateRecordingStartTime: (startTime: number | null) => void;
  isRecording: boolean;
  selectedAssetOnCanvas: { id: string; selectedAsset: AssetType };
  navMenuRef: React.RefObject<HTMLDivElement>;
  onAiAudioRequest: (data: any) => Promise<GeneratedAudioResult>;
  onAiBackgroundRequest: (data: any) => Promise<string>;
  onSaveAndCanvasImportBackground: (imageUrl: string) => void;
  onSaveAndCanvasImportAudio: (
    audioUrl: string,
    title?: string,
    duration?: number
  ) => void;
  lastSelectedCharacterIndex: string | null;
  videoId?: string;
  videoSas?: {
    token?: string;
    expiredAt?: number;
  };
  audioFiles: any[];
  setAudioFiles: (audioFiles: any[]) => void;
  riggedCharactersRef: React.MutableRefObject<Map<string, RiggedCharacterRef>>;
  onUpdateRiggedCharacter?: (
    id: string,
    properties: {
      position?: { x: number; y: number };
      scale?: number;
      pathPoints?: Position[];
    }
  ) => void;
  onOpenCharacterLibrary?: () => void;
  onOpenCharacterVideoGenerator?: () => void;
  onOpenLiveActionGenerator?: () => void;
  onOpenEnvironmentLibrary?: () => void;
}

const ToolBar: React.FC<ToolBarProps> = ({
  onBackgroundSelect,
  onAnimationSelect,
  onEffectSelect,
  onUploadedAssetSelect,
  onTextSelect,
  layers,
  handleLayerLock,
  onLayerDrag,
  selectedMenu,
  onMenuClick,
  onMenuClose,
  onAudioSelect,
  onUpdateCharacter,
  onUpdateText,
  charactersRef,
  textRef,
  onUpdateIsRecording,
  onUpdateRecordingStartTime,
  isRecording,
  selectedAssetOnCanvas,
  navMenuRef,
  onAiAudioRequest,
  onAiBackgroundRequest,
  onSaveAndCanvasImportBackground,
  onSaveAndCanvasImportAudio,
  lastSelectedCharacterIndex,
  videoId,
  videoSas,
  audioFiles,
  setAudioFiles,
  riggedCharactersRef,
  onUpdateRiggedCharacter,
  onOpenCharacterLibrary,
  onOpenCharacterVideoGenerator,
  onOpenLiveActionGenerator,
  onOpenEnvironmentLibrary,
}) => {
  const tweeningOptions = [
    {
      text: 'up',
      icon: <ArrowUpwardIcon style={{ color: 'white' }} />,
      movement: { x: 0, y: -400 },
      type: 'simple',
    },
    {
      text: 'down',
      icon: <ArrowDownwardIcon style={{ color: 'white' }} />,
      movement: { x: 0, y: 400 },
      type: 'simple',
    },
    {
      text: 'left',
      icon: <KeyboardBackspaceIcon style={{ color: 'white' }} />,
      movement: { x: -400, y: 0 },
      type: 'simple',
    },
    {
      text: 'right',
      icon: <ArrowRightAltIcon style={{ color: 'white' }} />,
      movement: { x: 400, y: 0 },
      type: 'simple',
    },
    // New complex animations
    {
      text: 'circle',
      icon: <LoopIcon style={{ color: 'white' }} />,
      radius: 150,
      duration: 2,
      type: 'complex',
      animationType: 'circle',
    },
    {
      text: 'stairs',
      icon: <TrendingDownIcon style={{ color: 'white' }} />,
      stepSize: { x: 100, y: 100 },
      steps: 4,
      type: 'complex',
      animationType: 'stairs',
    },
    {
      text: 'diagonal',
      icon: <SouthEastIcon style={{ color: 'white' }} />,
      movement: { x: 300, y: 300 },
      type: 'complex',
      animationType: 'diagonal',
    },
    {
      text: 'bounce',
      icon: <HeightIcon style={{ color: 'white' }} />,
      height: 200,
      bounces: 3,
      type: 'complex',
      animationType: 'bounce',
    },
    {
      type: 'record',
      icon: isRecording ? (
        <PauseIcon style={{ color: 'white' }} />
      ) : (
        <RecordIcon style={{ color: 'white' }} />
      ),
      label: isRecording ? 'Stop Recording' : 'Start Recording',
    },
    {
      type: 'playback',
      icon: <PlayArrowIcon style={{ color: 'white' }} />,
      label: 'Play Recording',
    },
  ];

  const getIcon = (menu: string) => {
    switch (menu) {
      case 'assets':
        return <CloudUploadIcon className="toolbar-icon" />;
      case 'backgrounds':
        return <WallpaperIcon className="toolbar-icon" />;
      case 'effects':
        return <MovieIcon className="toolbar-icon" />;
      case 'characters':
        return <PersonIcon className="toolbar-icon" />;
      case 'audio':
        return <AudiotrackIcon className="toolbar-icon" />;
      case 'props':
        return <BuildIcon className="toolbar-icon" />;
      case 'text':
        return <TextFieldsIcon className="toolbar-icon" />;
      case 'animations':
        return <AnimationIcon className="toolbar-icon" />;
      case 'riggedCharacters':
        return <PersonAddIcon className="toolbar-icon" />;
      case 'environments':
        return <LandscapeIcon className="toolbar-icon" />;
      case 'characterVideo':
        return <VideoCallIcon className="toolbar-icon" />;
      case 'liveAction':
        return <AutoAwesomeIcon className="toolbar-icon" />;
      default:
        return null;
    }
  };

  const hiddenTilesEnabled = isHiddenTilesEnabled();
  const visibleTiles = hiddenTilesEnabled
    ? TOOLBAR_TILES.filter((menu) => !TILES_HIDDEN_BY_FLAG.includes(menu))
    : TOOLBAR_TILES;

  return (
    <div className="toolbar-wrapper">
      {/* Toolbar on the left */}
          <div className="toolbar-container">
            {visibleTiles.map((menu) => (
              <div
                key={menu}
                className={`toolbar-section ${selectedMenu === menu ? 'selected' : ''}`}
                onClick={() => {
                  if (menu === 'riggedCharacters' && onOpenCharacterLibrary) {
                    onOpenCharacterLibrary();
                  } else if (
                    menu === 'environments' &&
                    onOpenEnvironmentLibrary
                  ) {
                    onOpenEnvironmentLibrary();
                  } else if (
                    menu === 'characterVideo' &&
                    onOpenCharacterVideoGenerator
                  ) {
                    onOpenCharacterVideoGenerator();
                  } else if (
                    menu === 'liveAction' &&
                    onOpenLiveActionGenerator
                  ) {
                    onOpenLiveActionGenerator();
                  } else {
                    onMenuClick(menu);
                  }
                }}
                title={
                  menu === 'riggedCharacters'
                    ? 'Character Studio'
                    : menu === 'environments'
                      ? 'Environments'
                      : menu === 'characterVideo'
                        ? 'Video Generation'
                        : menu === 'liveAction'
                          ? 'Live Action to Animation'
                          : menu.charAt(0).toUpperCase() + menu.slice(1)
                }
              >
                {getIcon(menu)}
                <span className="v3-label">
                  {TILE_LABEL[menu] ?? menu}
                </span>
              </div>
            ))}
      </div>

      {/* Expanding menu content */}
      {selectedMenu &&
        (!hiddenTilesEnabled || !TILES_HIDDEN_BY_FLAG.includes(selectedMenu)) && (
        <Menu
          tweeningOptions={tweeningOptions}
          selectedMenu={selectedMenu}
          onClose={onMenuClose}
          onAnimationSelect={onAnimationSelect}
          onBackgroundSelect={onBackgroundSelect}
          onEffectSelect={onEffectSelect}
          onUploadedAssetSelect={onUploadedAssetSelect}
          onTextSelect={onTextSelect}
          onAudioSelect={onAudioSelect}
          layers={layers}
          handleLayerLock={handleLayerLock}
          onLayerDrag={onLayerDrag}
          onUpdateCharacter={onUpdateCharacter}
          onUpdateText={onUpdateText}
          charactersRef={charactersRef}
          textRef={textRef}
          onUpdateIsRecording={onUpdateIsRecording}
          onUpdateRecordingStartTime={onUpdateRecordingStartTime}
          isRecording={isRecording}
          selectedAssetOnCanvas={selectedAssetOnCanvas}
          navMenuRef={navMenuRef}
          onAiAudioRequest={onAiAudioRequest}
          onAiBackgroundRequest={onAiBackgroundRequest}
          onSaveAndCanvasImportBackground={onSaveAndCanvasImportBackground}
          onSaveAndCanvasImportAudio={onSaveAndCanvasImportAudio}
          lastSelectedCharacterIndex={lastSelectedCharacterIndex}
          videoId={videoId}
          videoSas={videoSas}
          audioFiles={audioFiles}
          setAudioFiles={setAudioFiles}
          riggedCharactersRef={riggedCharactersRef}
          onUpdateRiggedCharacter={onUpdateRiggedCharacter}
        />
      )}
    </div>
  );
};

export default ToolBar;
